'use client';

import { FC, useState } from 'react';

import { LocalizedRoutes } from '@ravago/shared/page-data/models/pages';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useSearchParams } from 'next/navigation';
import { CookiesProvider } from 'react-cookie';

import { Country, Language } from '../../../../services/contentful/locale.service';
import { getCountryCode, getLanguageCode } from '../../../../utils/locale.utils';
import { regionCookieOptions } from '../../region-modal/utils/region-preference.utils';
import { useRegionPreference } from '../../region-modal/use-region-preference';
import CountrySwitch from '../../country-switch/country-switch';
import LanguageSwitch from '../../language-switch/language-switch';

interface Props {
  locale: string;
  availableCountries: Array<Country>;
  availableLanguages: Array<Language>;
  localizedRoutes?: LocalizedRoutes;
}

const CountryLanguageMenuInner: FC<Props> = ({ locale, availableCountries, availableLanguages, localizedRoutes }) => {
  const { phone, tabPort } = useBreakpoint();
  const translate = useTranslations();
  const searchParams = useSearchParams();
  const { setPreference } = useRegionPreference();

  const [selectedCountryCode, setSelectedCountryCode] = useState(() => getCountryCode(locale));
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(() => getLanguageCode(locale));

  const handleConfirm = () => {
    localStorage.setItem('country-key', selectedCountryCode);
    setPreference({ language: selectedLanguageCode, country: selectedCountryCode });

    const localizedPath = localizedRoutes?.[selectedLanguageCode];
    const newRoute = localizedPath
      ? `/${selectedLanguageCode}-${selectedCountryCode}/${localizedPath}`
      : `/${selectedLanguageCode}-${selectedCountryCode}`;

    const query = searchParams.toString();
    window.location.assign(query ? `${newRoute}?${query}` : newRoute);
  };

  return (
    <Box gap="xl" spacing={{ x: !phone && !tabPort ? 'lg' : 'none' }} direction="column" width={!phone && !tabPort ? '408px' : '100%'} height="100%">
      {availableCountries.length > 1 && (
        <CountrySwitch
          countries={availableCountries}
          selectedCountryCode={selectedCountryCode}
          onCountrySelect={setSelectedCountryCode}
        />
      )}
      {availableLanguages.length > 1 && (
        <LanguageSwitch
          languages={availableLanguages}
          selectedLanguageCode={selectedLanguageCode}
          onLanguageSelect={setSelectedLanguageCode}
        />
      )}
      <Button variant="primary" onClick={handleConfirm}>
        {translate('page-consumer.header.country-selector.confirm')}
      </Button>
    </Box>
  );
};

const CountryLanguageMenu: FC<Props> = (props) => (
  <CookiesProvider defaultSetOptions={regionCookieOptions}>
    <CountryLanguageMenuInner {...props} />
  </CookiesProvider>
);

export default CountryLanguageMenu;
